import { useQuery } from "@tanstack/react-query";

import {
  IEmailTemplate,
  IEmailTemplateType,
} from "../../../@types/admin/AdminEmailTemplate";
import { emailTemplatesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export function emailTemplateKey(
  wardId: number | undefined,
  templateType: IEmailTemplateType | ""
) {
  return ["admin-email-template", wardId, templateType];
}

/**
 * One ward's template of one type. Idle until both are chosen — the endpoint
 * 404s without them, and Retool likewise only ran once its two selects were
 * set.
 */
export function useEmailTemplate(
  wardId: number | undefined,
  templateType: IEmailTemplateType | ""
) {
  const { data, isLoading, isError, isFetched } =
    useQuery<IEmailTemplate | null>({
      queryKey: emailTemplateKey(wardId, templateType),
      queryFn: async () => {
        const response = await emailTemplatesService.getEmailTemplate(
          wardId!,
          templateType as IEmailTemplateType
        );
        // A ward without a template of this type is a legitimate empty state
        // (the controller answers 404), not a failure to load.
        if (response?.status === 404) return null;
        return ensureOk(response);
      },
      enabled: wardId !== undefined && templateType !== "",
      // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
      // and the global QueryClient disables refetch-on-mount with a 3-minute
      // staleTime — so re-opening the page must re-read the template.
      refetchOnMount: "always",
    });

  return { emailTemplate: data, isLoading, isError, isFetched };
}
